import { useState, useEffect, useCallback, useRef } from 'react';
import { useOnlineStatus } from './useOnlineStatus';
import {
  getQueueItems,
  getQueueCount,
  getFailedQueueCount,
  removeFromQueue,
  updateQueueItem,
  clearFailedQueueItems as clearFailedQueueItemsStorage,
  OfflineQueueItem,
  MAX_QUEUE_ATTEMPTS,
} from '@/lib/offlineStorage';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { setHapticsSuppressed } from '@/hooks/useHaptics';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import type { QueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Singleton state — shared across every useOfflineSync() caller so we don't
// double-run sync loops or race-update the pending count.
// ---------------------------------------------------------------------------

type Subscriber = (snapshot: SyncSnapshot) => void;

interface SyncSnapshot {
  pendingCount: number;
  failedCount: number;
  isSyncing: boolean;
}

const state: SyncSnapshot = {
  pendingCount: 0,
  failedCount: 0,
  isSyncing: false,
};

const subscribers = new Set<Subscriber>();
let syncInProgress = false;
let lastAutoSyncAt = 0;
const AUTO_SYNC_COOLDOWN_MS = 30_000;

function emit() {
  const snap = { ...state };
  subscribers.forEach((cb) => cb(snap));
}

async function refreshCounts() {
  const [pending, failed] = await Promise.all([getQueueCount(), getFailedQueueCount()]);
  state.pendingCount = pending;
  state.failedCount = failed;
  emit();
}

const supportedTables = [
  'workouts', 'food_entries', 'water_entries', 'mood_logs',
  'weight_entries', 'body_measurements',
] as const;
type SupportedTable = typeof supportedTables[number];

async function processQueueItem(item: OfflineQueueItem): Promise<{ ok: boolean; error?: string }> {
  try {
    const { table, operation, data } = item;
    if (!supportedTables.includes(table as SupportedTable)) {
      return { ok: false, error: `Unsupported table: ${table}` };
    }
    const t = table as SupportedTable;

    switch (operation) {
      case 'insert': {
        const { error } = await supabase
          .from(t)
          .upsert(data as never, { onConflict: 'id', ignoreDuplicates: true });
        if (error) throw error;
        break;
      }
      case 'update': {
        const { id, ...updateData } = data;
        const { error } = await supabase
          .from(t)
          .update(updateData as never)
          .eq('id', id as string);
        if (error) throw error;
        break;
      }
      case 'delete': {
        const { error } = await supabase.from(t).delete().eq('id', data.id as string);
        if (error) throw error;
        break;
      }
    }
    return { ok: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[offlineSync] queue item failed:', { id: item.id, table: item.table, operation: item.operation, error: message });
    return { ok: false, error: message };
  }
}

async function runSync(queryClient: QueryClient, isOnline: boolean, opts: { force?: boolean } = {}) {
  if (syncInProgress || !isOnline) return;

  if (!opts.force) {
    const since = Date.now() - lastAutoSyncAt;
    if (since < AUTO_SYNC_COOLDOWN_MS) return;
  }

  syncInProgress = true;
  // Replayed queue writes must never buzz — only interactive logs do.
  setHapticsSuppressed(true);
  state.isSyncing = true;
  lastAutoSyncAt = Date.now();
  emit();

  try {
    const items = await getQueueItems();
    if (items.length === 0) return;

    let failCount = 0;
    let newlyFailedCount = 0;
    const syncedTables = new Set<string>();

    for (const item of items) {
      const result = await processQueueItem(item);
      if (result.ok) {
        await removeFromQueue(item.id);
        syncedTables.add(item.table);
      } else {
        failCount++;
        const attempts = (item.attempts ?? 0) + 1;
        if (attempts >= MAX_QUEUE_ATTEMPTS) {
          await updateQueueItem(item.id, { attempts, lastError: result.error, failed: true });
          newlyFailedCount++;
        } else {
          await updateQueueItem(item.id, { attempts, lastError: result.error });
        }
      }
    }

    // Invalidate caches for tables that successfully synced.
    if (syncedTables.has('workouts')) queryClient.invalidateQueries({ queryKey: queryKeys.workouts });
    if (syncedTables.has('food_entries')) queryClient.invalidateQueries({ queryKey: queryKeys.foodEntries });
    if (syncedTables.has('water_entries')) queryClient.invalidateQueries({ queryKey: queryKeys.waterEntries });
    
    if (syncedTables.has('mood_logs')) queryClient.invalidateQueries({ queryKey: queryKeys.moodLogs });
    if (syncedTables.has('weight_entries')) queryClient.invalidateQueries({ queryKey: queryKeys.weightEntries });
    if (syncedTables.has('body_measurements')) queryClient.invalidateQueries({ queryKey: queryKeys.bodyMeasurements });
    if (syncedTables.size > 0) {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
      queryClient.invalidateQueries({ queryKey: queryKeys.weeklyWorkoutStats });
    }

    if (newlyFailedCount > 0) {
      toast.error(
        `${newlyFailedCount} change${newlyFailedCount > 1 ? 's' : ''} couldn't sync after ${MAX_QUEUE_ATTEMPTS} tries. Tap "Clear failed" in the banner to dismiss.`
      );
    } else if (failCount > 0) {
      // Quieter than a toast — retry next online edge.
      console.warn(`[offlineSync] ${failCount} item(s) failed; will retry later`);
    }
  } catch (error) {
    console.error('[offlineSync] sync loop crashed:', error);
  } finally {
    await refreshCounts();
    state.isSyncing = false;
    syncInProgress = false;
    setHapticsSuppressed(false);
    emit();
  }
}

// Initial count probe
let initialized = false;
function ensureInitialized() {
  if (initialized) return;
  initialized = true;
  refreshCounts().catch(() => {});
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOfflineSync() {
  const { isOnline } = useOnlineStatus();
  const queryClient = useQueryClient();
  const [snapshot, setSnapshot] = useState<SyncSnapshot>(state);
  const wasOnlineRef = useRef(isOnline);

  // Subscribe to singleton
  useEffect(() => {
    ensureInitialized();
    const cb: Subscriber = (next) => setSnapshot(next);
    subscribers.add(cb);
    setSnapshot({ ...state });
    return () => {
      subscribers.delete(cb);
    };
  }, []);

  // Auto-sync ONLY on offline→online transitions (not on every pendingCount change).
  useEffect(() => {
    const wasOnline = wasOnlineRef.current;
    wasOnlineRef.current = isOnline;
    if (!wasOnline && isOnline) {
      runSync(queryClient, isOnline).catch(() => {});
    }
  }, [isOnline, queryClient]);

  const syncPending = useCallback(async () => {
    // Manual click bypasses the cooldown.
    await runSync(queryClient, isOnline, { force: true });
  }, [isOnline, queryClient]);

  const clearFailed = useCallback(async () => {
    await clearFailedQueueItemsStorage();
    await refreshCounts();
  }, []);

  const refreshPendingCount = useCallback(async () => {
    await refreshCounts();
  }, []);

  return {
    pendingCount: snapshot.pendingCount,
    failedCount: snapshot.failedCount,
    isSyncing: snapshot.isSyncing,
    syncPending,
    clearFailed,
    refreshPendingCount,
  };
}
